/**
 * @fileoverview Mailbox agent-persona settings (MOR-529).
 *
 * Centered, sectioned settings layout: mailbox address card, agent persona
 * card (bound to a local form; Save enabled only when dirty), and the current
 * user's per-job notification opt-ins (saved immediately on toggle).
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { AtSign, Bot, BellRing, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Switch } from "@/ui/primitives/switch";
import { Textarea } from "@/ui/primitives/textarea";
import { extractErrorMessage } from "@/utils/errors";
import { DeleteMailboxDialog } from "./DeleteMailboxDialog";
import { htmlToPlainText, plainTextToHtml } from "./htmlBody";
import { SignatureEditor } from "./SignatureEditor";
import type { Mailbox, MailPrefs } from "./types";

/** Shared card chrome for each settings section. */
function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: typeof Bot;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-card">
      <div className="flex items-start gap-3 border-b bg-muted/30 px-5 py-4">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background">
          <Icon className="size-4 text-primary" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="space-y-5 px-5 py-5">{children}</div>
    </section>
  );
}

export function MailboxSettingsTab({ mailbox }: { mailbox: Mailbox }) {
  const updateSettings = useMutation(api.mail.mutations.updateMailboxSettings);

  // Text fields hold the per-mailbox OVERRIDE; blank means "inherit the
  // workspace default" (shown as the placeholder). Toggles show the
  // effective value and saving sets an explicit per-mailbox override.
  const overrides = mailbox.overrides;
  const [agentName, setAgentName] = useState(overrides.agentName ?? "");
  const [senderName, setSenderName] = useState(overrides.senderName ?? "");
  const [agentTone, setAgentTone] = useState(overrides.agentTone ?? "");
  const [agentInstructions, setAgentInstructions] = useState(
    overrides.agentInstructions ?? "",
  );
  // One HTML value backs the signature editor; legacy text-only signatures
  // are lifted to HTML so they open editable. Save writes BOTH parts (the
  // HTML plus derived plain text) — the send pipeline appends one per MIME
  // part.
  const savedSignatureHtml = useMemo(
    () =>
      overrides.signatureHtml ??
      (overrides.signatureText ? plainTextToHtml(overrides.signatureText) : ""),
    [overrides],
  );
  const [signatureHtml, setSignatureHtml] = useState(savedSignatureHtml);
  const [autoDraftReplies, setAutoDraftReplies] = useState(
    mailbox.autoDraftReplies,
  );
  const [autoFollowUps, setAutoFollowUps] = useState(mailbox.autoFollowUps);
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(
    () =>
      agentName !== (overrides.agentName ?? "") ||
      senderName !== (overrides.senderName ?? "") ||
      agentTone !== (overrides.agentTone ?? "") ||
      agentInstructions !== (overrides.agentInstructions ?? "") ||
      signatureHtml !== savedSignatureHtml ||
      autoDraftReplies !== mailbox.autoDraftReplies ||
      autoFollowUps !== mailbox.autoFollowUps,
    [
      agentName,
      senderName,
      agentTone,
      agentInstructions,
      signatureHtml,
      savedSignatureHtml,
      autoDraftReplies,
      autoFollowUps,
      overrides,
      mailbox,
    ],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({
        mailboxId: mailbox._id,
        agentName: agentName.trim() || null,
        senderName: senderName.trim() || null,
        agentTone: agentTone.trim() || null,
        agentInstructions: agentInstructions.trim() || null,
        signatureHtml: signatureHtml.trim() || null,
        signatureText: htmlToPlainText(signatureHtml).trim() || null,
        autoDraftReplies,
        autoFollowUps,
      });
      toast.success("Mailbox settings saved");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save settings"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 pb-10">
      <SettingsSection
        icon={AtSign}
        title="Mailbox"
        description="This job's dedicated email address."
      >
        <div className="rounded-lg border bg-muted/40 px-4 py-3">
          <p className="font-mono text-sm font-medium text-foreground">
            {mailbox.emailAddress}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Mail sent to this address lands in the Inbox tab and can trigger
            Morgan to draft a reply.
          </p>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Bot}
        title="Agent persona"
        description="How Morgan writes and signs mail on this job. Blank fields inherit the workspace defaults from the main Mail settings."
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="agent-name">Agent name</Label>
            <Input
              id="agent-name"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              maxLength={60}
              placeholder={mailbox.agentName}
            />
            <p className="text-xs text-muted-foreground">
              Used when Morgan drafts and signs replies. Leave blank to use the
              workspace default.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agent-tone">Tone</Label>
            <Input
              id="agent-tone"
              value={agentTone}
              onChange={(e) => setAgentTone(e.target.value)}
              placeholder={mailbox.agentTone ?? "e.g. formal, warm, concise"}
            />
            <p className="text-xs text-muted-foreground">
              Optional style guidance for drafts.
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sender-name">Sender name</Label>
          <Input
            id="sender-name"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            maxLength={100}
            placeholder={
              mailbox.senderName ??
              `${agentName.trim() || mailbox.agentName} — company name`
            }
          />
          <p className="text-xs text-muted-foreground">
            The From name recipients see on outgoing mail. Leave blank to use
            the workspace default (or agent name, a dash, then the company
            name).
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="agent-instructions">Instructions</Label>
          <Textarea
            id="agent-instructions"
            value={agentInstructions}
            onChange={(e) => setAgentInstructions(e.target.value)}
            rows={5}
            maxLength={5000}
            placeholder={
              mailbox.agentInstructions ??
              "Guidance for how the agent should handle mail on this job."
            }
          />
        </div>

        <div className="space-y-1.5">
          <Label>Signature</Label>
          <SignatureEditor
            idPrefix="mailbox"
            value={signatureHtml}
            onChange={setSignatureHtml}
          />
          <p className="text-xs text-muted-foreground">
            Appended to every email this mailbox sends. Paste a signature from
            Outlook, Gmail, or Word into the editor, or type and format it here.
            Leave blank to use the workspace default.
          </p>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
          <div className="space-y-0.5">
            <Label htmlFor="auto-draft">Auto-draft replies</Label>
            <p className="text-xs text-muted-foreground">
              When on, inbound mail triggers Morgan to draft a reply for your
              review.
            </p>
          </div>
          <Switch
            id="auto-draft"
            checked={autoDraftReplies}
            onCheckedChange={setAutoDraftReplies}
          />
        </div>

        <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
          <div className="space-y-0.5">
            <Label htmlFor="auto-follow-ups">Proactive follow-ups</Label>
            <p className="text-xs text-muted-foreground">
              Morgan periodically reviews unanswered threads (e.g. a director
              sitting on a POD form) and prepares follow-up drafts for your
              approval. Nothing sends without you.
            </p>
          </div>
          <Switch
            id="auto-follow-ups"
            checked={autoFollowUps}
            onCheckedChange={setAutoFollowUps}
          />
        </div>

        <div className="flex justify-end border-t pt-4">
          <Button onClick={handleSave} disabled={!dirty || saving}>
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </SettingsSection>

      <MailNotificationsSection jobId={mailbox.jobId} />

      <DangerZoneSection mailbox={mailbox} />
    </div>
  );
}

/**
 * Destructive actions (MOR-954): permanently delete the inbox and all of its
 * mail. The two-step dialog (warning, then type-the-address) gates the
 * mutation; the server re-checks the typed address as a backstop.
 */
function DangerZoneSection({ mailbox }: { mailbox: Mailbox }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <SettingsSection
      icon={Trash2}
      title="Danger zone"
      description="Permanently delete this inbox and everything in it."
    >
      <div className="flex flex-col gap-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium text-foreground">Delete inbox</p>
          <p className="max-w-prose text-xs leading-relaxed text-muted-foreground">
            Deletes{" "}
            <span className="break-all font-mono text-[0.95em]">
              {mailbox.emailAddress}
            </span>{" "}
            and every thread, message, and draft. This cannot be undone; you can
            set up a new inbox with a different address afterwards.
          </p>
        </div>
        <Button
          variant="destructive"
          size="sm"
          className="w-full shrink-0 sm:w-auto"
          onClick={() => setDialogOpen(true)}
        >
          <Trash2 className="mr-1.5 size-3.5" />
          Delete inbox
        </Button>
      </div>

      <DeleteMailboxDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mailboxId={mailbox._id}
        emailAddress={mailbox.emailAddress}
      />
    </SettingsSection>
  );
}

/**
 * Per-job CC/forward opt-ins for the current user. Toggling saves immediately
 * (both flags are sent together, as `setJobMailPrefs` requires). The global
 * default is shown as a hint; a job override wins over it.
 */
function MailNotificationsSection({ jobId }: { jobId: Id<"jobs"> }) {
  const prefs = useQuery(api.mail.ccRules.getMyMailPrefs, { jobId }) as
    MailPrefs | null | undefined;
  const setJobMailPrefs = useMutation(api.mail.ccRules.setJobMailPrefs);
  const [saving, setSaving] = useState(false);

  // The job override wins; fall back to the effective (global) values so the
  // switches reflect what actually happens today even before an override is set.
  const ccOutbound =
    prefs?.job?.ccOutbound ?? prefs?.effective.ccOutbound ?? false;
  const forwardInbound =
    prefs?.job?.forwardInbound ?? prefs?.effective.forwardInbound ?? false;

  const save = async (next: {
    ccOutbound: boolean;
    forwardInbound: boolean;
  }) => {
    setSaving(true);
    try {
      await setJobMailPrefs({ jobId, ...next });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update notifications"));
    } finally {
      setSaving(false);
    }
  };

  const globalHint = prefs?.global
    ? `Your global default is CC ${prefs.global.ccOutbound ? "on" : "off"}, Forward ${
        prefs.global.forwardInbound ? "on" : "off"
      }.`
    : "Your global default is CC off, Forward off.";
  const usingOverride = Boolean(prefs?.job);

  return (
    <SettingsSection
      icon={BellRing}
      title="Your notifications"
      description={`${globalHint} ${
        usingOverride
          ? "This job overrides your global default."
          : "Toggles here override it for this job."
      }`}
    >
      <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="space-y-0.5">
          <Label htmlFor="cc-outbound">CC me on outgoing mail</Label>
          <p className="text-xs text-muted-foreground">
            You are added to the Cc of every email this mailbox sends.
          </p>
        </div>
        <Switch
          id="cc-outbound"
          checked={ccOutbound}
          disabled={saving || prefs === undefined}
          onCheckedChange={(checked) =>
            void save({ ccOutbound: checked, forwardInbound })
          }
        />
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="space-y-0.5">
          <Label htmlFor="forward-inbound">
            Forward incoming mail to my inbox
          </Label>
          <p className="text-xs text-muted-foreground">
            Every inbound message is forwarded to your account email.
          </p>
        </div>
        <Switch
          id="forward-inbound"
          checked={forwardInbound}
          disabled={saving || prefs === undefined}
          onCheckedChange={(checked) =>
            void save({ ccOutbound, forwardInbound: checked })
          }
        />
      </div>
    </SettingsSection>
  );
}
